import {ChangeDetectionStrategy, Component, computed, inject, OnInit} from '@angular/core';
import {
  CodebookService,
  disposalScheduleToOption,
  DocumentSearchService,
  DocumentsTableComponent,
  DocumentView,
  ELASTIC_RELOAD_DELAY,
  EntityClassSelectorComponent,
  EntityClassSelectorUsage,
  isDocumentEntity,
  RetentionTriggerTypeWidgetComponent,
} from '|shared';
import {ReactiveFormsModule} from '@angular/forms';
import {
  CheckboxComponent,
  DatePickerComponent,
  FormAutocompleteComponent,
  IczFormContainerDirective,
  IczFormControl,
  IczFormGroup,
  IczOption,
  IczValidators
} from '@icz/angular-form-elements';
import {
  AlertComponent,
  ButtonComponent,
  FormButtonsComponent,
  LabelComponent,
  LoadingIndicatorService,
  SectionComponent,
  TabDirective,
  TabItem,
  TabsComponent,
  WaitingDirective
} from '@icz/angular-essentials';
import {IczModalService, IczModalSizeClass, injectModalData, injectModalRef} from '@icz/angular-modal';
import {ApiEntityClassService, ReclassifyResultDto} from '|api/codebook';
import {FilterOperator, FilterTreeOperator, SearchParams} from '@icz/angular-table';
import {
  CheckUnsavedFormDialogService,
  IczFormSubmitDirective,
  IFormGroupCheckable
} from '@icz/angular-application-core';
import {
  EntityClassReclassificationObjectsDatasource,
  EntityStateForReclassification
} from './entity-class-reclassification-objects.datasource';
import {DocumentDto, FileDto} from '|api/document';
import {EntityClassDto, EntityType, EsslSearchRequestBody, OperationEntityDto} from '|api/commons';
import {delay} from 'rxjs/operators';
import {
  EntityClassReclassificationResultDialogComponent
} from './entity-class-reclassification-result-dialog/entity-class-reclassification-result-dialog.component';
import {CurrentSessionService, DocumentToastService, DocumentToastType, isAdmin} from '|ui-essl-core';
import {AsyncPipe} from '@angular/common';

enum EntityClassResortingTab {
  DEFINITION = 'DEFINITION',
  CONTENT = 'CONTENT',
}

export interface EntityClassReclassificationDialogData {
  entityClassId: number;
}

@Component({
  selector: 'icz-entity-class-resorting-dialog',
  templateUrl: './entity-class-reclassification-dialog.component.html',
  styleUrls: ['./entity-class-reclassification-dialog.component.scss'],
  providers: [
    CheckUnsavedFormDialogService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, IczFormContainerDirective, WaitingDirective, FormButtonsComponent, ButtonComponent, IczFormSubmitDirective, TabsComponent, TabDirective, EntityClassSelectorComponent, SectionComponent, DatePickerComponent, RetentionTriggerTypeWidgetComponent, FormAutocompleteComponent, DocumentsTableComponent, LabelComponent, CheckboxComponent, AlertComponent, AsyncPipe]
})
export class EntityClassReclassificationDialogComponent implements OnInit, IFormGroupCheckable {
  protected loadingService = inject(LoadingIndicatorService);
  private modalRef = injectModalRef<Nullable<boolean>>();
  protected dialogData = injectModalData<EntityClassReclassificationDialogData>();
  private checkUnsavedService = inject(CheckUnsavedFormDialogService);
  private codebookService = inject(CodebookService);
  private documentSearchService = inject(DocumentSearchService);
  private apiEntityClassService = inject(ApiEntityClassService);
  private documentToastService = inject(DocumentToastService);
  private currentSessionService = inject(CurrentSessionService);
  private modalService = inject(IczModalService);

  form = new IczFormGroup({
    resortAllObjects: new IczFormControl<Nullable<boolean>>(false),
    targetEntityClassId: new IczFormControl<Nullable<number>>(null, [IczValidators.required()]),
    sourceEntityClassId: new IczFormControl<Nullable<number>>({value: null, disabled: true}),
    targetEntityClassValidFrom: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    sourceEntityClassValidTo: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    targetEntityClassDisposalScheduleId: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    sourceEntityClassDisposalScheduleId: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    reclassifyObjectsInStoringUnits: new IczFormControl<Nullable<boolean>>(false),
    reclassifyObjectsInRegistryOffice: new IczFormControl<Nullable<boolean>>(false),
  });
  formGroupsToCheck!: string[];
  currentActiveTab = EntityClassResortingTab.DEFINITION;
  disposalScheduleOptions: IczOption[] = [];
  dataSource: Nullable<EntityClassReclassificationObjectsDatasource>;
  selectedObjects: (DocumentDto | FileDto)[] = [];
  selectedObjectsCount = 0;
  currentSearchParams: Nullable<SearchParams>;
  isAllSelected = false;

  isAdmin = computed(() => isAdmin(this.currentSessionService.currentUserFunctionalPosition()!));

  tabs: TabItem[] = [
    {
      id: EntityClassResortingTab.DEFINITION,
      label: 'Přetřídění',
    },
    {
      id: EntityClassResortingTab.CONTENT,
      label: 'Přetřizované objekty ve věcné skupině',
    }
  ];
  readonly EntityClassResortingTab = EntityClassResortingTab;

  get sourceEntityClassControl() {
    return this.form.get('sourceEntityClassId') as IczFormControl;
  }

  get sourceEntityClassDisposalScheduleIdControl() {
    return this.form.get('sourceEntityClassDisposalScheduleId') as IczFormControl;
  }

  get targetEntityClassDisposalScheduleIdControl() {
    return this.form.get('targetEntityClassDisposalScheduleId') as IczFormControl;
  }

  get reclassifyObjectsInStoringUnits() {
    return this.form.get('reclassifyObjectsInStoringUnits')!.value!;
  }

  get reclassifyObjectsInRegistryOffice() {
    return this.form.get('reclassifyObjectsInRegistryOffice')!.value!;
  }

  get usageBasedOnRole() {
    return this.isAdmin() ? EntityClassSelectorUsage.ADMIN_CURRENT_WITH_V : EntityClassSelectorUsage.ADMIN_HISTORIC_WITH_V;
  }

  ngOnInit() {
    this.checkUnsavedService.addUnsavedFormCheck(this, ['form']);
    this.sourceEntityClassControl.setValue(this.dialogData.entityClassId);
    this.loadingService.startLoading(this);
    this.codebookService.disposalSchedules().subscribe(disposalSchedules => {
      this.disposalScheduleOptions = disposalSchedules.map(disposalScheduleToOption);
      this.loadingService.endLoading(this);
    });
  }

  get entityClassId() {
    return this.form.get('targetEntityClassId')!.value;
  }

  onTargetEntityClassChange(entityClass: Nullable<EntityClassDto>) {
    if (entityClass) {
      this.form.get('targetEntityClassValidFrom')!.setValue(entityClass.validFrom);
      this.targetEntityClassDisposalScheduleIdControl.setValue(entityClass.disposalScheduleId);
    }
  }

  onSourceEntityClassChange(entityClass: Nullable<EntityClassDto>) {
    if (entityClass) {
      this.form.get('sourceEntityClassValidTo')!.setValue(entityClass.validTo);
      this.sourceEntityClassDisposalScheduleIdControl.setValue(entityClass.disposalScheduleId);
    }
  }

  selectAllChanged(isSelected: boolean) {
    this.isAllSelected = isSelected;
  }

  tabChanged(selectedTab: TabItem<string | number>) {
    this.currentActiveTab = selectedTab.id as EntityClassResortingTab;
    this.dataSource = new EntityClassReclassificationObjectsDatasource(
      this.documentSearchService,
      this.isAdmin() ? EntityStateForReclassification.BOTH : EntityStateForReclassification.CLOSED,
      this.dialogData.entityClassId,
      this.reclassifyObjectsInStoringUnits,
      this.reclassifyObjectsInRegistryOffice
    );
    this.selectedObjects = [];
    this.selectedObjectsCount = 0;
    this.currentSearchParams = null;
  }

  searchParamsSelected(searchParams: SearchParams) {
    this.selectedObjects = [];
    this.selectedObjectsCount = 0;
    this.currentSearchParams = searchParams;
  }

  selectionChanged(selection: (DocumentDto | FileDto)[]) {
    this.selectedObjects = selection;
    this.selectedObjectsCount = selection.length;
    this.currentSearchParams = null;
  }

  get enableReclassification() {
    return this.selectedObjectsCount > 0 || this.isAllSelected;
  }

  submit() {
    const formValue = this.form.getRawValue();
    const entitiesToReclassify: OperationEntityDto[] = [];
    let objectFilter: Nullable<EsslSearchRequestBody> = null;

    if (this.selectedObjectsCount > 0) {
      this.selectedObjects.forEach(object => {
        if (isDocumentEntity(object.entityType)) {
          entitiesToReclassify.push({
            entityType: EntityType.DOCUMENT,
            id: object.id,
          });
        } else {
          entitiesToReclassify.push({
            entityType: EntityType.FILE,
            id: object.id,
          });
        }
      });
    } else if (this.currentSearchParams) {
      objectFilter = {};

      if (!this.reclassifyObjectsInStoringUnits) {
        this.currentSearchParams.filter.push({
          fieldName: 'storageUnitId',
          value: null,
          operator: FilterOperator.empty
        });
      }

      if (!this.reclassifyObjectsInRegistryOffice) {
        this.currentSearchParams.filter.push({
          fieldName: 'inRegistryOffice',
          value: false,
          operator: FilterOperator.equals
        });
      }

      objectFilter!.complexFilter = {
        operator: FilterTreeOperator.AND,
        values: this.currentSearchParams.filter,
      };
    }

    this.loadingService.doLoading(
      this.apiEntityClassService.entityClassReclassify({
        body: {
          entities: entitiesToReclassify,
          objectFilter,
          targetEntityClassId: formValue.targetEntityClassId!
        }
      }),
      this
    ).pipe(delay(ELASTIC_RELOAD_DELAY)).subscribe(result => {
      if (!result.valid) {
        this.modalService.openComponentInModal<boolean, ReclassifyResultDto>({
          component: EntityClassReclassificationResultDialogComponent,
          modalOptions: {
            sizeClass: IczModalSizeClass.WH_800_600,
            titleTemplate: 'Pretřídění věcné skupiny - chybové objekty',
          },
          data: result
        }).subscribe(result => {
          if (result) {
            this.form.markAsPristine();
            this.modalRef.close(true);
          }
        });
      } else {
        this.documentToastService.dispatchSimpleInfoToast(DocumentToastType.ENTITY_CLASS_RECLASSIFICATION_SUCCESS);
        this.form.markAsPristine();
        this.modalRef.close(true);
      }
    });
  }

  close() {
    this.modalRef.forceClose(null);
  }

  protected readonly DocumentView = DocumentView;
  protected readonly EntityClassSelectorUsage = EntityClassSelectorUsage;
}
